import { useEffect, useMemo } from 'react';
import { matchPath, useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import { SidebarTabNames } from '@pages/Admin/Campaign/ui/constants';
import { SidebarTabNamesType } from '@pages/Admin/Campaign/ui/types';
import { CampaignDto } from '@shared/services/apiService/apiService';
import { AuthenticatedPath } from '@shared/constants/routes.constants';

interface UseEditCampaignTabRoutingOptions {
  campaignData?: CampaignDto;
  isSquadCampaign: boolean;
}

const resolveCampaignTab = (tab: string): SidebarTabNamesType | null => {
  const resolvedTab = tab === 'tools' ? SidebarTabNames.SETTINGS : tab;

  return Object.values(SidebarTabNames).includes(resolvedTab as SidebarTabNamesType)
    ? (resolvedTab as SidebarTabNamesType)
    : null;
};

export const useEditCampaignTabRouting = ({
  campaignData,
  isSquadCampaign,
}: UseEditCampaignTabRoutingOptions) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const isEditCampaignRoute = Boolean(
    matchPath({ path: AuthenticatedPath.EDIT_CAMPAIGN_V2, end: true }, location.pathname),
  );

  const activeTab = useMemo((): SidebarTabNamesType | null => {
    if (!isEditCampaignRoute) return null;

    const tab = searchParams.get('tab');
    if (!tab) {
      return null;
    }

    const resolvedTab = resolveCampaignTab(tab);
    if (!resolvedTab) return null;

    if (resolvedTab === SidebarTabNames.SQUADS && campaignData && !isSquadCampaign) {
      return SidebarTabNames.AGENT;
    }

    return resolvedTab;
  }, [searchParams, campaignData, isEditCampaignRoute, isSquadCampaign]);

  useEffect(() => {
    if (!isEditCampaignRoute) return;

    const tab = searchParams.get('tab');
    if (!tab) {
      navigate(`${window.location.pathname}?tab=${encodeURIComponent(SidebarTabNames.GENERAL)}`, {
        replace: true,
      });
      return;
    }

    const resolvedTab = resolveCampaignTab(tab);
    if (!resolvedTab) {
      navigate(`${window.location.pathname}?tab=${encodeURIComponent(SidebarTabNames.GENERAL)}`, {
        replace: true,
      });
      return;
    }

    if (resolvedTab === SidebarTabNames.SQUADS && campaignData && !isSquadCampaign) {
      navigate(`${window.location.pathname}?tab=${encodeURIComponent(SidebarTabNames.AGENT)}`, {
        replace: true,
      });
    }
  }, [campaignData, isEditCampaignRoute, isSquadCampaign, navigate, searchParams]);

  return activeTab;
};
